import {
  HiOutlineBars3,
  HiChevronLeft,
  HiChevronRight,
  HiOutlineDocumentText,
  HiOutlineHome,
  HiOutlineMagnifyingGlass,
  HiOutlineTrash,
  HiXMark,
} from 'react-icons/hi2'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { formatPrice, useCart } from '@/context/CartContext'
import { useFavorites } from '@/context/FavoritesContext'
import { useCompare } from '@/context/CompareContext'
import { useAuth } from '@/context/AuthContext'
import { useWarehouse } from '@/context/WarehouseContext'
import { useNotifications } from '@/context/NotificationsContext'
import LogoSvg from '../Logo'
import { Cart, Compare, Heart, Profile } from '@/icons'
import CatalogDropdown from '@/components/CatalogDropdown'
import { catalogApi, searchApi, type CategoryTreeNode, type SearchSuggestion } from '@/api/endpoints'
import { useDebounce } from '@/hooks/useDebounce'
import CitySelector from '@/components/CitySelector'
import WarehouseBadge from '@/components/WarehouseBadge'
import { useWarehouseScopedUrl } from '@/features/warehouse/useWarehouseScopedUrl'

const Header = () => {
  const MOBILE_SIDEBAR_ANIMATION_MS = 250
  const { items, itemCount, selectedSubtotal, removeItem } = useCart()
  const { favoriteCount } = useFavorites()
  const { compareCount } = useCompare()
  const { isAuthenticated } = useAuth()
  const { unreadCount } = useNotifications()
  const { warehouses, selectedWarehouseId, setSelectedWarehouse } = useWarehouse()
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [isCatalogOpen, setIsCatalogOpen] = useState(false)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)
  const [isMobileSidebarMounted, setIsMobileSidebarMounted] = useState(false)
  const [mobileCategories, setMobileCategories] = useState<CategoryTreeNode[]>([])
  const [mobileCatalogStack, setMobileCatalogStack] = useState<CategoryTreeNode[]>([])
  const [mobileCatalogLoading, setMobileCatalogLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const withWarehouse = useWarehouseScopedUrl()
  const [searchValue, setSearchValue] = useState('')
  const debouncedSearch = useDebounce(searchValue, 1000)
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)
  const [suggestionsFetched, setSuggestionsFetched] = useState(false)
  const cartRef = useRef<HTMLDivElement>(null)
  const catalogRef = useRef<HTMLDivElement>(null)
  const mobileSearchRef = useRef<HTMLDivElement>(null)
  const desktopSearchRef = useRef<HTMLFormElement>(null)
  const mobileSidebarCloseTimeoutRef = useRef<number | null>(null)
  const catalogCloseTimeoutRef = useRef<number | null>(null)

  const closeSearch = () => setShowSuggestions(false)

  const openCatalogDropdown = () => {
    if (catalogCloseTimeoutRef.current) {
      window.clearTimeout(catalogCloseTimeoutRef.current)
      catalogCloseTimeoutRef.current = null
    }
    setIsCatalogOpen(true)
  }

  const scheduleCatalogClose = () => {
    if (catalogCloseTimeoutRef.current) {
      window.clearTimeout(catalogCloseTimeoutRef.current)
    }
    catalogCloseTimeoutRef.current = window.setTimeout(() => {
      setIsCatalogOpen(false)
      catalogCloseTimeoutRef.current = null
    }, 140)
  }

  const openMobileSidebar = () => {
    if (mobileSidebarCloseTimeoutRef.current) {
      window.clearTimeout(mobileSidebarCloseTimeoutRef.current)
      mobileSidebarCloseTimeoutRef.current = null
    }
    setMobileCatalogStack([])
    setIsMobileSidebarMounted(true)
    requestAnimationFrame(() => setIsMobileSidebarOpen(true))
  }

  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false)
    if (mobileSidebarCloseTimeoutRef.current) {
      window.clearTimeout(mobileSidebarCloseTimeoutRef.current)
    }
    mobileSidebarCloseTimeoutRef.current = window.setTimeout(() => {
      setIsMobileSidebarMounted(false)
      setMobileCatalogStack([])
      mobileSidebarCloseTimeoutRef.current = null
    }, MOBILE_SIDEBAR_ANIMATION_MS)
  }

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      const isInsideSearch =
        mobileSearchRef.current?.contains(target) || desktopSearchRef.current?.contains(target)

      if (cartRef.current && !cartRef.current.contains(e.target as Node)) {
        setIsCartOpen(false)
      }
      if (catalogRef.current && !catalogRef.current.contains(e.target as Node)) {
        setIsCatalogOpen(false)
      }
      if (!isInsideSearch) {
        closeSearch()
      }
    }
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsCartOpen(false)
        setIsCatalogOpen(false)
        closeMobileSidebar()
        closeSearch()
      }
    }
    const onScrollStart = () => {
      closeSearch()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onEscape)
    window.addEventListener('scroll', onScrollStart, true)
    window.addEventListener('wheel', onScrollStart, { passive: true })
    window.addEventListener('touchmove', onScrollStart, { passive: true })
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onEscape)
      window.removeEventListener('scroll', onScrollStart, true)
      window.removeEventListener('wheel', onScrollStart)
      window.removeEventListener('touchmove', onScrollStart)
      if (mobileSidebarCloseTimeoutRef.current) {
        window.clearTimeout(mobileSidebarCloseTimeoutRef.current)
      }
      if (catalogCloseTimeoutRef.current) {
        window.clearTimeout(catalogCloseTimeoutRef.current)
      }
    }
  }, [])

  const submitSearch = (raw: string, closeMobile = false) => {
    const q = raw.trim()
    if (!q) return
    closeSearch()
    if (closeMobile) {
      closeMobileSidebar()
    }
    const params = new URLSearchParams()
    params.set('q', q)
    navigate(withWarehouse(`/catalog?${params.toString()}`))
  }

  useEffect(() => {
    const q = debouncedSearch.trim()
    if (q.length < 2) {
      setSuggestions([])
      setSuggestionsLoading(false)
      setSuggestionsFetched(false)
      return
    }
    setSuggestionsLoading(true)
    setSuggestionsFetched(false)
    searchApi
      .getSuggestions(q, 5)
      .then((res) => {
        setSuggestions(res)
      })
      .catch(() => setSuggestions([]))
      .finally(() => {
        setSuggestionsLoading(false)
        setSuggestionsFetched(true)
      })
  }, [debouncedSearch])

  useEffect(() => {
    setMobileCatalogLoading(true)
    catalogApi
      .getCategoriesTree()
      .then((data) => {
        setMobileCategories(data)
      })
      .catch(() => {
        setMobileCategories([])
      })
      .finally(() => setMobileCatalogLoading(false))
  }, [selectedWarehouseId])

  useEffect(() => {
    closeSearch()
  }, [location.pathname, location.search])

  const mobileCatalogCurrentParent =
    mobileCatalogStack.length > 0 ? mobileCatalogStack[mobileCatalogStack.length - 1] : null
  const mobileCatalogShownItems = mobileCatalogCurrentParent?.children ?? mobileCategories
  const showWarehouseSelector = warehouses.length > 0
  const isMobileNavActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)

  const mobileNavItems = [
    { label: 'Главная', path: '/', icon: HiOutlineHome },
    { label: 'Каталог', path: '/catalog', icon: HiOutlineBars3 },
    { label: 'Корзина', path: '/cart', icon: Cart, badge: itemCount },
    { label: 'Избранное', path: '/favorites', icon: Heart, badge: favoriteCount },
    {
      label: isAuthenticated ? 'Профиль' : 'Войти',
      path: isAuthenticated ? '/profile' : '/login',
      icon: Profile,
      badge: unreadCount,
    },
  ]

  return (
    <header ref={catalogRef} className="bg-white">
      {showWarehouseSelector ? (
        <div className="hidden lg:block border-b border-gray-100">
          <div className="container mx-auto flex h-9 items-center text-xs">
            <div className="flex-1 w-full min-w-0 container mx-auto px-4 2xl:px-0 py-4 box-border">
              <CitySelector
                variant="topbar"
                value={selectedWarehouseId}
                options={warehouses}
                onChange={setSelectedWarehouse}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className="container relative mx-auto px-4 lg:px-0 py-4 flex justify-between items-center gap-6">
        <div className="flex items-center gap-3 shrink-0 lg:gap-4">
          <a href="/" className="flex items-center gap-1.5 shrink-0 px-4 lg:px-0">
            <LogoSvg className='w-17 2xl:w-29.25' />
          </a>
        </div>

        <div ref={mobileSearchRef} className="lg:hidden w-full sm:w-[50%]">
          <form
            className="relative w-full"
            onSubmit={(e) => {
              e.preventDefault()
              submitSearch(searchValue, true)
            }}
          >
            <input
              type="search"
              placeholder="Найти"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value)
                setShowSuggestions(true)
              }}
              onFocus={() => setShowSuggestions(true)}
              className="w-full bg-white rounded-lg border border-gray-200 pl-4 pr-12 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-primary-light focus:border-primary h-12"
            />
            <button
              type="submit"
              className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500"
              aria-label="Найти"
            >
              <HiOutlineMagnifyingGlass className="w-5 h-5" />
            </button>
          </form>
          {showSuggestions && searchValue.trim().length >= 2 && (
            <div className="absolute z-50 mt-4 left-0 right-0 w-full mx-auto bg-white shadow-lg border border-gray-200 max-h-80 overflow-auto">
              {suggestionsLoading ? (
                <div className="px-3 py-3 text-sm text-gray-500">Ищем…</div>
              ) : suggestions.length > 0 ? (
                <ul className="py-1">
                  {suggestions.map((s) => (
                    <li key={s.warehouseScopedId}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left hover:bg-gray-50"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          closeSearch()
                          setSearchValue(s.name)
                          closeMobileSidebar()
                          navigate(s.productPath)
                        }}
                      >
                        <span className="block text-sm font-medium text-gray-900">{s.name}</span>
                        <span className="mt-1 flex">
                          <WarehouseBadge city={s.warehouseName} />
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : suggestionsFetched ? (
                <div className="px-3 py-3 text-sm text-gray-500">
                  По вашему запросу нет результатов
                </div>
              ) : null}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={openMobileSidebar}
          className="lg:hidden inline-flex items-center justify-center p-2 rounded-lg border border-gray-200 text-gray-900 hover:bg-gray-50 transition-colors"
          aria-label="Открыть меню"
        >
          <HiOutlineBars3 className="w-6 h-6" />
        </button>

        <div className="hidden select-none flex-1 lg:flex min-w-50 items-center max-w-158 h-12">
          <div className="shrink-0 flex flex-row w-full min-w-0 relative">
            <div
              className="shrink-0"
              onMouseEnter={openCatalogDropdown}
              onMouseLeave={scheduleCatalogClose}
            >
              <button
                type="button"
                onClick={openCatalogDropdown}
                className={`flex items-center gap-2 px-5 h-12 rounded-bl-lg rounded-tl-lg  font-medium transition-colors shrink-0 ${isCatalogOpen
                  ? 'bg-primary-hover text-white'
                  : 'bg-primary text-white hover:bg-primary-hover'
                  }`}
              >
                <HiOutlineBars3 className="w-5 h-5" />
                Каталог
              </button>
            </div>
            <div className="flec flex-col w-full">
              <form
                ref={desktopSearchRef}
                className="flex-1 min-w-0 relative"
                onSubmit={(e) => {
                  e.preventDefault()
                  submitSearch(searchValue)
                }}
              >
                <input
                  type="search"
                  placeholder="Найти"
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value)
                    setShowSuggestions(true)
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  className="w-full bg-white rounded-br-lg rounded-tr-lg border border-gray-200 pl-4 pr-12 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-primary-light focus:border-primary h-12 ml-0"
                />
                <button
                  type="submit"
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500"
                  aria-label="Найти"
                >
                  <HiOutlineMagnifyingGlass className="w-5 h-5" />
                </button>
                {showSuggestions && searchValue.trim().length >= 2 && (
                  <div className="absolute z-50 mt-1 w-full rounded-lg bg-white shadow-lg border border-gray-200 max-h-80 overflow-auto">
                    {suggestionsLoading ? (
                      <div className="px-3 py-3 text-sm text-gray-500">Ищем…</div>
                    ) : suggestions.length > 0 ? (
                      <ul className="py-1">
                        {suggestions.map((s) => (
                          <li key={s.warehouseScopedId}>
                            <button
                              type="button"
                              className="w-full px-3 py-2 text-left hover:bg-gray-50"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                closeSearch()
                                setSearchValue(s.name)
                                navigate(s.productPath)
                              }}
                            >
                              <span className="block text-sm font-medium text-gray-900">{s.name}</span>
                              <span className="mt-1 flex">
                                <WarehouseBadge city={s.warehouseName} />
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : suggestionsFetched ? (
                      <div className="px-3 py-3 text-sm text-gray-500">
                        По вашему запросу нет результатов
                      </div>
                    ) : null}
                  </div>
                )}
              </form>
            </div>
          </div>


          {isCatalogOpen && (
            <CatalogDropdown
              onClose={() => setIsCatalogOpen(false)}
              onMouseEnter={openCatalogDropdown}
              onMouseLeave={scheduleCatalogClose}
            />
          )}
        </div>

        <nav className="hidden lg:flex items-center gap-6 shrink-0">
          {isAuthenticated ? (
            <Link
              to={withWarehouse('/profile')}
              className="flex flex-col items-center gap-1 text-black hover:text-primary transition-colors"
            >
              <span className="relative inline-flex">
                <Profile />
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-3 min-w-5 h-4 px-1 rounded-full bg-primary text-white text-[10px] leading-4 text-center">
                    +{unreadCount}
                  </span>
                )}
              </span>
              <span className="hidden lg:flex text-sm font-normal">Профиль</span>
            </Link>
          ) : (
            <Link
              to={withWarehouse('/login')}
              className="flex flex-col items-center gap-1 text-black hover:text-primary transition-colors"
            >
              <Profile />
              <span className="hidden lg:flex text-sm font-normal">Войти</span>
            </Link>
          )}
          <Link
            to={withWarehouse('/favorites')}
            className="flex flex-col items-center gap-1 text-black hover:text-primary transition-colors"
          >
            <span className="relative inline-flex">
              <Heart className="text-black" />
              {favoriteCount > 0 && (
                <span className="absolute -top-2 -right-2 min-w-4 h-4 px-1 rounded-full bg-primary text-white text-[10px] leading-4 text-center">
                  {favoriteCount}
                </span>
              )}
            </span>
            <span className="hidden lg:flex text-sm font-normal">Избранное</span>
          </Link>
          <Link
            to={withWarehouse('/compare')}
            className="flex flex-col items-center gap-1 text-black hover:text-primary transition-colors"
          >
            <span className="relative inline-flex">
              <Compare />
              {compareCount > 0 && (
                <span className="absolute -top-2 -right-2 min-w-4 h-4 px-1 rounded-full bg-primary text-white text-[10px] leading-4 text-center">
                  {compareCount}
                </span>
              )}
            </span>
            <span className="hidden lg:flex text-sm font-normal">Сравнение</span>
          </Link>
          <Link
            to={withWarehouse('/analyzer')}
            className="flex flex-col items-center gap-1 text-black hover:text-primary transition-colors"
          >
            <HiOutlineDocumentText className="w-6 h-6 text-black" />
            <span className="hidden lg:flex text-sm font-normal">Panic-Full Анализатор</span>
          </Link>
          <div ref={cartRef} className="relative">
            <button
              type="button"
              onClick={() => setIsCartOpen((prev) => !prev)}
              className="flex flex-col items-center gap-1 text-black hover:text-primary transition-colors cursor-pointer"
            >
              <span className="relative inline-flex">
                <Cart />
                {itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 min-w-4 h-4 px-1 rounded-full bg-primary text-white text-[10px] leading-4 text-center">
                    {itemCount}
                  </span>
                )}
              </span>
              <span className="hidden lg:flex text-sm font-normal">Корзина</span>
            </button>

            {isCartOpen && (
              <div className="absolute right-0 mt-3 w-88 bg-white border border-gray-200 rounded-xl shadow-xl z-50">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Корзина</h3>
                  <span className="text-sm text-gray-500">{itemCount} шт.</span>
                </div>

                <div className="max-h-72 overflow-auto">
                  {items.length === 0 ? (
                    <p className="px-4 py-6 text-sm text-gray-500">Корзина пока пустая</p>
                  ) : (
                    items.map((item) => (
                      <div key={item.id} className="px-4 py-3 border-b border-gray-100 flex gap-3">
                        <div className="w-12 h-12 rounded-md border border-gray-200 bg-gray-50 overflow-hidden shrink-0">
                          {item.image ? (
                            <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                          ) : null}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-900 line-clamp-2">{item.title}</p>
                          <p className="text-xs text-gray-500 mt-1">Количество: {item.quantity}</p>
                          <p className="text-sm font-semibold text-gray-900 mt-1">
                            {formatPrice(item.price)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="text-gray-400 hover:text-red-500"
                          aria-label="Удалить товар"
                        >
                          <HiOutlineTrash className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Итого</span>
                    <span className="font-semibold text-gray-900">{formatPrice(selectedSubtotal)}</span>
                  </div>
                  <div className="w-full">
                    <Link
                      to={withWarehouse('/cart')}
                      onClick={() => setIsCartOpen(false)}
                      className="h-10 w-full rounded-md bg-primary text-white font-medium inline-flex items-center justify-center hover:bg-primary-hover transition-colors"
                    >
                      Оформить
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>
      </div>

      {isMobileSidebarMounted && (
        <div
          className={`lg:hidden fixed inset-0 z-50 transition-opacity duration-250 ease-out ${isMobileSidebarOpen ? 'opacity-100' : 'opacity-0'
            }`}
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40 transition-opacity duration-250 ease-out"
            onClick={closeMobileSidebar}
            aria-label="Закрыть меню"
          />

          <aside
            className={`absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white shadow-2xl flex flex-col transition-transform duration-250 ease-out ${isMobileSidebarOpen ? 'translate-x-0' : 'translate-x-full'
              }`}
          >
            <div className="h-16 px-4 border-b border-gray-100 flex items-center justify-between">
              <span className="text-base font-semibold text-gray-900">Меню</span>
              <button
                type="button"
                onClick={closeMobileSidebar}
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                aria-label="Закрыть меню"
              >
                <HiXMark className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-auto space-y-4">
              {showWarehouseSelector ? (
                <CitySelector
                  variant="mobile"
                  value={selectedWarehouseId}
                  options={warehouses}
                  onChange={setSelectedWarehouse}
                />
              ) : null}

              <div className="space-y-2">
                <Link
                  to={withWarehouse('/catalog')}
                  onClick={closeMobileSidebar}
                  className="h-11 px-3 rounded-lg border border-gray-200 flex items-center gap-3 text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  <HiOutlineBars3 className="w-5 h-5" />
                  <span>Каталог</span>
                </Link>
                <Link
                  to={withWarehouse('/analyzer')}
                  onClick={closeMobileSidebar}
                  className="h-11 px-3 rounded-lg border border-gray-200 flex items-center gap-3 text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  <HiOutlineDocumentText className="w-5 h-5" />
                  <span>Panic-Full Анализатор</span>
                </Link>
                <Link
                  to={withWarehouse(isAuthenticated ? '/profile' : '/login')}
                  onClick={closeMobileSidebar}
                  className="h-11 px-3 rounded-lg border border-gray-200 flex items-center justify-between text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  <span className="inline-flex items-center gap-3">
                    <Profile />
                    {isAuthenticated ? 'Профиль' : 'Войти'}
                  </span>
                  {isAuthenticated && unreadCount > 0 && (
                    <span className="min-w-5 h-5 px-1 rounded-full bg-primary text-white text-[11px] leading-5 text-center">
                      +{unreadCount}
                    </span>
                  )}
                </Link>
                <Link
                  to={withWarehouse('/favorites')}
                  onClick={closeMobileSidebar}
                  className="h-11 px-3 rounded-lg border border-gray-200 flex items-center justify-between text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  <span className="inline-flex items-center gap-3">
                    <Heart className="text-black" />
                    Избранное
                  </span>
                  {favoriteCount > 0 && (
                    <span className="min-w-5 h-5 px-1 rounded-full bg-primary text-white text-[11px] leading-5 text-center">
                      {favoriteCount}
                    </span>
                  )}
                </Link>
                <Link
                  to={withWarehouse('/compare')}
                  onClick={closeMobileSidebar}
                  className="h-11 px-3 rounded-lg border border-gray-200 flex items-center justify-between text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  <span className="inline-flex items-center gap-3">
                    <Compare />
                    Сравнение
                  </span>
                  {compareCount > 0 && (
                    <span className="min-w-5 h-5 px-1 rounded-full bg-primary text-white text-[11px] leading-5 text-center">
                      {compareCount}
                    </span>
                  )}
                </Link>
                <Link
                  to={withWarehouse('/cart')}
                  onClick={closeMobileSidebar}
                  className="h-11 px-3 rounded-lg border border-gray-200 flex items-center justify-between text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  <span className="inline-flex items-center gap-3">
                    <Cart />
                    Корзина
                  </span>
                  {itemCount > 0 && (
                    <span className="min-w-5 h-5 px-1 rounded-full bg-primary text-white text-[11px] leading-5 text-center">
                      {itemCount}
                    </span>
                  )}
                </Link>
              </div>

              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <div className="h-11 px-3 bg-gray-50 flex items-center justify-between">
                  {mobileCatalogCurrentParent ? (
                    <button
                      type="button"
                      onClick={() => setMobileCatalogStack((prev) => prev.slice(0, -1))}
                      className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900 transition-colors"
                    >
                      <HiChevronLeft className="w-4 h-4" />
                      Назад
                    </button>
                  ) : (
                    <span className="text-sm font-semibold text-gray-900">Каталог</span>
                  )}
                  {mobileCatalogCurrentParent ? (
                    <span className="text-sm font-medium text-gray-900 truncate max-w-45">
                      {mobileCatalogCurrentParent.title}
                    </span>
                  ) : null}
                </div>

                <div className="p-2">
                  {mobileCatalogLoading ? (
                    <div className="px-2 py-3 text-sm text-gray-500">Загрузка...</div>
                  ) : mobileCatalogShownItems.length === 0 ? (
                    <div className="px-2 py-3 text-sm text-gray-500">Категории не найдены</div>
                  ) : (
                    <ul className="space-y-1 overflow-y-auto h-62.5">
                      {mobileCatalogShownItems.map((category) => {
                        const hasChildren = (category.children?.length ?? 0) > 0
                        return (
                          <li key={category.slug} className="flex items-center gap-1">
                            <Link
                              to={withWarehouse(`/catalog?category=${category.slug}`)}
                              onClick={closeMobileSidebar}
                              className="flex-1 min-w-0 h-10 px-3 rounded-md text-sm text-gray-900 hover:bg-gray-50 transition-colors inline-flex items-center"
                            >
                              <span className="truncate">{category.title}</span>
                            </Link>
                            {hasChildren && (
                              <button
                                type="button"
                                onClick={() => setMobileCatalogStack((prev) => [...prev, category])}
                                className="h-10 w-10 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors inline-flex items-center justify-center"
                                aria-label={`Открыть подкатегории ${category.title}`}
                              >
                                <HiChevronRight className="w-4 h-4" />
                              </button>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}

      <nav
        aria-label="Основная навигация"
        className="lg:hidden fixed inset-x-0 bottom-0 z-1000 border-t border-gray-100 bg-white/95 shadow-[0_-8px_24px_rgba(15,23,42,0.10)] backdrop-blur supports-[padding:max(0px)]:pb-[env(safe-area-inset-bottom)]"
      >
        <div className="mx-auto grid h-[76px] max-w-lg grid-cols-5 items-stretch px-1">
          {mobileNavItems.map(({ label, path, icon: Icon, badge }) => {
            const active =
              isMobileNavActive(path) && !(label === 'Поиск' && location.pathname !== '/catalog')
            return (
              <Link
                key={label}
                to={withWarehouse(path)}
                className={`relative flex min-w-0 flex-col items-center justify-center gap-1 px-1 text-[11px] transition-colors ${
                  active ? 'text-primary' : 'text-gray-700 hover:text-primary'
                }`}
                aria-current={active ? 'page' : undefined}
              >
                <span className="relative flex h-8 items-center justify-center">
                  <Icon className="h-7 w-7" />
                  {badge ? (
                    <span className="absolute -right-3 -top-1 min-w-4 rounded-full bg-primary px-1 text-center text-[10px] leading-4 text-white">
                      {badge}
                    </span>
                  ) : null}
                </span>
                <span className="truncate leading-4">{label}</span>
              </Link>
            )
          })}
        </div>
      </nav>
    </header>
  )
}

export default Header
